/* eslint-disable import/no-anonymous-default-export */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable multiline-comment-style */
import React, { useEffect, useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import PageTabs from '@/components/PageTabs'
import { getI18nTime, replaceMoneySymbol } from '@/common/utils/i18nFuncs'
import { TagPro } from '@roo/roo'
import TipText from '@/components/TipText'
import CommonTable from '@/components/CommonTable'
import { fetchBillList, fetchFundSummary } from './utils/api'
import { IBillItem } from './utils/types'
import DownloadBill from './components/DownloadBill'
import FundOverview from './components/FundOverview'
import FundTransactions from './components/FundTransactions'
import aggregateQueryAtom from '@/common/recoil/atom/aggregateQuery'
import { getYearMonthPattern } from '@/common/utils/i18n'

const { Pane: TabPane } = PageTabs

export default () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('fund')
  const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 20 })
  const { data: billListRes, loading, run } = useRequest(() => fetchBillList({ ...pageInfo }), { manual: true })
  const { data: fundOverviewData, loading: fundOverviewLoading, run: runFundSummary } = useRequest(() => fetchFundSummary(), { manual: true })
  const updateAggregateQueryInfo = aggregateQueryAtom.useRefresh()
  const [updateEndTime, setUpdateEndTime] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (activeTab === 'bill') {
      run()
      setUpdateEndTime(undefined)
    } else if (activeTab === 'fund') {
      runFundSummary()
    }
  }, [pageInfo, activeTab]) // 分页信息改变或切换tab时,重新请求对应数据

  // 获取资金概览数据后，刷新企业配置（包括角标状态）
  useEffect(() => {
    if (fundOverviewData) {
      updateAggregateQueryInfo()
      setUpdateEndTime(fundOverviewData?.updateEndTime)
    }
  }, [fundOverviewData])

  const getBillStatusColor = (billStatus: IBillItem['settlementStatementStatus']) => {
    switch (billStatus) {
      case 'success':
        return '#CCCCCC' // 已结清 - 灰色
      case 'partPay':
        return '#FF8800' // 部分支付 - 橙色
      case 'waitPay':
        return 'brand' // 待支付 - 品牌色
      case 'noNeedPay':
        return '#1890FF' // 无需支付 - 蓝色
      default:
        return 'brand'
    }
  }

  const getBillStatusText = (billStatus: IBillItem['settlementStatementStatus']) => {
    switch (billStatus) {
      case 'success':
        return t('Keeta_EE_Clearance_YwJJ', '已结清')
      case 'partPay':
        return t('Keeta_EE_payment_partial_E9xD', '部分支付')
      case 'waitPay':
        return t('Keeta_EE_Payment_DSAW', '待支付')
      case 'noNeedPay':
        return t('Keeta_EE_Payments_without_the_need_uKbO', '无需支付')
      default:
        return billStatus
    }
  }

  const billColumns = [
    {
      title: t('e_pc_period', '账单周期'),
      dataIndex: 'effectiveTimeRule',
      width: 180,
      render: (_:any, record: IBillItem) =>  {
        const formater = getYearMonthPattern()
        const startTime = getI18nTime(record.startDate, formater)
        const endTime = getI18nTime(record.endDate, formater)
        return `${startTime} ~ ${endTime}`
      },
    },
    {
      title: t('e_pc_bill_no', '账单编号'),
      dataIndex: 'billNo',
      width: 180,
      render: (text:string) => <TipText text={text} />,
    },
    {
      title: t('Keeta_EE_Name_Billing_MoAW', '账单名称'),
      dataIndex: 'startDate',
      width: 180,
      render: (text: number) => {
        const formater = getYearMonthPattern()
        const startTime = getI18nTime(text, formater)
        return <TipText text={t('Keeta_EE_Billing_cWRD', { date: startTime, periodType: t('Keeta_EE_Billing_Monthly') }) || '--'} />
      },
    },
    {
      title: t('e_pc_status', '账单状态'),
      dataIndex: 'settlementStatementStatus',
      width: 120,
      render: (_:string, record: IBillItem) => record?.settlementStatementStatus ? (
        <TagPro color={getBillStatusColor(record.settlementStatementStatus)}>
          {getBillStatusText(record.settlementStatementStatus)}
        </TagPro>
      ) : ('--'),
    },
    {
      title: t('e_pc_settlement_amount', '结算金额'),
      dataIndex: 'entSettleAmount',
      width: 150,
      render: (_:string, record: IBillItem) => <TipText text={replaceMoneySymbol(record.entSettleAmount?.displayText || '--')} />,
    },
    {
      title: t('Keeta_EE_Payment_Amount_fYzR', '未支付金额'),
      dataIndex: 'settlementStatementBalance',
      width: 150,
      render: (settlementStatementBalance: any) => <TipText text={replaceMoneySymbol(settlementStatementBalance?.displayText || '--')} />,
    },
    {
      title: t('e_pc_operation', '操作'),
      dataIndex: 'rulePackageId',
      fixed: 'right',
      width: 120,
      render: (_:any, record: IBillItem) => <DownloadBill record={record}/>,
    },
  ].filter(Boolean) as any[]

  const handleTabClick = (name: string) => {
    setActiveTab(name)
  }

  return (
    <div className="page-content" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="page-title">
        {t('Keeta_EE_Financial_management_67HA', '财务管理')}
      </div>
      <PageTabs
        style={{ marginTop: '24px' }}
        value={activeTab}
        onTabClick={handleTabClick}
        size="small"
      >
        <TabPane label={t('Keeta_EE_Funding_vdMR', '资金')} name="fund" />
        <TabPane label={t('Keeta_EE_Billing_k7wM', '账单')} name="bill" />
      </PageTabs>
      {
        activeTab === 'fund' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, paddingTop: 24 }}>
            <FundOverview
              data={fundOverviewData}
              loading={fundOverviewLoading}
              onViewBill={() => setActiveTab('bill')}
            />
            <FundTransactions
              visible={activeTab === 'fund'}
              updateEndTime={updateEndTime}
            />
          </div>
        ) || null
      }
      {
        activeTab === 'bill' && (
          <div style={{ paddingTop: 24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <CommonTable
              rowKey="rulePackageId"
              columns={billColumns}
              data={billListRes?.result || []}
              loading={loading}
              pagination={{
                total: billListRes?.totalCount,
                pageSize: pageInfo.pageSize,
                onChange: (pageNo: number, pageSize: number) => setPageInfo({ pageNo, pageSize }),
              }}
            />
          </div>
        ) || null
      }
    </div>
  )
}
